import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Box, Typography, Stack, IconButton, Divider, Alert } from '@mui/material';
import { colors } from '../../utils/colorsGrafica';

const CardGrafica = ({ data, title, id, onRemoveSensor, sensor = false, idGrafica }) => {
  const { pathname } = useLocation();

  const getSize = () => {
    const width = window.innerWidth;
    if (width > 1000) return { width: 850, height: '' };
    if (width <= 1000 && width > 600) return { width: 600, height: 350 };
    if (width <= 600 && width > 450) return { width: 350, height: 300 };
    if (width <= 450) return { width: 305, height: 350 };
    return { width: 350, height: 300 };
  };

  const [empty, setEmpty] = useState('');
  const [sizeGraph, setSizeGraph] = useState(getSize());
  const [state, setState] = useState(undefined);

  useEffect(() => {
    const handleResize = () => setSizeGraph(getSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const listDate = () => {
    const listDate = [];
    data?.forEach((element) => {
      element?.data?.forEach((item) => listDate.push(item.date));
    });
    return [...new Set(listDate)];
  };

  const formatData = (dataGrafica) => {
    const list = listDate()?.map((item) => ({
      x: item,
      y: dataGrafica.find((i) => i?.date === item)?.value || null,
    }));
    const arrayOrdenado = list.sort((a, b) => new Date(a.x) - new Date(b.x));
    return {
      listNull: arrayOrdenado,
      list: arrayOrdenado.map((item) => ({
        x: new Date(item.x).getTime(), // Usar timestamp para ApexCharts
        y: item.y,
      })),
    };
  };

  const emptyData = () => {
    const dataEmpty = data.filter((item) => item.data && item.data.length !== 0);
    if (dataEmpty.length === 0) {
      setEmpty('El sensor no tiene datos disponibles');
      return true;
    }
    return false;
  };

  const parsedData = (dataGraph) => {
    const list = [];
    const listNull = [];
    dataGraph?.forEach((item) => {
      if (item.data && item.data.length > 0) {
        const formatted = formatData(item.data);
        list.push({ name: item.nombre, data: formatted.list });
        listNull.push(formatted.listNull);
      }
    });
    return { list, listNull };
  };

  const setMarkers = () => {
    const markers = [];
    const parsedResult = parsedData(data);
    parsedResult.listNull.forEach((serie, sIndex) => {
      serie.forEach((point, pIndex) => {
        markers.push({
          seriesIndex: sIndex,
          dataPointIndex: pIndex,
          size: point.y === null ? 0 : sizeGraph.width < 450 ? 1 : 3,
          fillColor: colors[sIndex] || '#008FFB',
          strokeColor: colors[sIndex] || '#008FFB',
        });
      });
    });
    return markers;
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      setEmpty('No hay datos de sensores para mostrar');
      setState(null);
      return;
    }

    const isEmpty = emptyData();
    if (isEmpty) {
      setState(null);
      return;
    }

    const parsedResult = parsedData(data);
    
    // Obtener unidades del primer sensor que tenga datos
    const firstSensorWithData = data.find(sensor => sensor.data && sensor.data.length > 0);
    const unidades = firstSensorWithData?.unidades || firstSensorWithData?.tipo?.unidades || 'Unidad';

    setState({
      series: parsedResult.list,
      options: {
        chart: { 
          zoom: { enabled: window.innerWidth > 600 },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          }
        },
        legend: { show: true },
        markers: { discrete: setMarkers() },
        stroke: { width: 2, curve: 'smooth' },
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false,
            format: 'dd/MM/yyyy HH:mm'
          }
        },
        yaxis: {
          title: {
            text: `Unidad (${unidades})`,
            style: { fontSize: '14px', fontWeight: 400 },
          },
        },
        tooltip: {
          x: {
            format: 'dd/MM/yyyy HH:mm'
          }
        },
        responsive: [
          {
            breakpoint: 1000,
            options: {
              legend: { position: 'top' },
            },
          },
        ],
        colors,
        grid: {
          show: true,
          borderColor: '#e0e0e0',
          strokeDashArray: 3,
        },
      },
    });
  }, [data, sizeGraph]);

  const handleDelete = (index) => onRemoveSensor(index);

  // Verificar si hay datos válidos
  const hasValidData = data && data.length > 0 && data.some(sensor => sensor.data && sensor.data.length > 0);

  return (
    <Box sx={{ width: '100%', p: 2, boxShadow: 3, borderRadius: 2, bgcolor: 'white', mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Typography variant="h6">{title}</Typography>
        {!pathname.includes('/info-grafica') && idGrafica && (
          <Link to={`/info-grafica/${idGrafica}`}>
            <InfoCircleOutlined style={{ fontSize: '18px', color: '#1976d2' }} />
          </Link>
        )}
      </Stack>

      <Box sx={{ mt: 2 }}>
        {hasValidData && state ? (
          <ReactApexChart
            options={state.options}
            series={state.series}
            type="line"
            width="100%"
            height={sizeGraph.height || 350}
          />
        ) : (
          <Alert severity="info" sx={{ textAlign: 'center' }}>
            {empty || 'No hay datos disponibles para mostrar en la gráfica'}
          </Alert>
        )}
      </Box>

      {/* Información adicional del sensor */}
      {sensor && data && data.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">{data[0].tipo?.nombre}</Typography>
          <Divider sx={{ my: 1 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {data[0].rangoMin && (
              <Box>
                <Typography variant="caption">Rango Min.</Typography>
                <Typography>{data[0].rangoMin.$numberDecimal || data[0].rangoMin}</Typography>
              </Box>
            )}
            {data[0].rangoMax && (
              <Box>
                <Typography variant="caption">Rango Max.</Typography>
                <Typography>{data[0].rangoMax.$numberDecimal || data[0].rangoMax}</Typography>
              </Box>
            )}
            {data[0].posicionEnTrama && (
              <Box>
                <Typography variant="caption">Posición</Typography>
                <Typography>{data[0].posicionEnTrama}</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {/* Lista de sensores en modo edición */}
      {pathname.includes('info-grafica') && data?.map((item) => (
        <Stack
          key={item[id]}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2, p: 1, border: '1px solid #ccc', borderRadius: 1 }}
        >
          <Box>
            <Typography>{item.nombre}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.data ? `${item.data.length} datos` : 'Sin datos'}
            </Typography>
          </Box>
          <Popconfirm
            title="¿Estás seguro que deseas eliminar el sensor?"
            onConfirm={() => handleDelete(item[id])}
            okText="Sí"
            cancelText="Cancelar"
            placement="right"
          >
            <IconButton><CloseOutlined /></IconButton>
          </Popconfirm>
        </Stack>
      ))}
    </Box>
  );
};

CardGrafica.propTypes = {
  title: PropTypes.string,
  idGrafica: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    nombre: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.number,
      date: PropTypes.string
    })),
    unidades: PropTypes.string,
    tipo: PropTypes.shape({
      nombre: PropTypes.string,
      unidades: PropTypes.string
    })
  })),
  id: PropTypes.string,
  onRemoveSensor: PropTypes.func,
  sensor: PropTypes.bool,
};

CardGrafica.defaultProps = {
  title: '',
  idGrafica: '',
  data: [],
  id: '_id',
  sensor: false,
  onRemoveSensor: () => {},
};

export default CardGrafica;